import { useEffect, useState } from 'react';

const PROBE_PATH = '/api/system/health';
const PROBE_INTERVAL_MS = 15_000;
const PROBE_TIMEOUT_MS = 5_000;

async function probeApi(): Promise<boolean> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    await fetch(PROBE_PATH, { method: 'GET', signal: controller.signal, cache: 'no-store' });
    return true;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

export function useOnlineStatus(): boolean {
  const [isOnline, setIsOnline] = useState<boolean>(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );

  useEffect(() => {
    let cancelled = false;

    function handleOnline() {
      setIsOnline(true);
    }
    function handleOffline() {
      setIsOnline(false);
    }

    async function runProbe() {
      if (cancelled) return;
      if (!navigator.onLine) {
        setIsOnline(false);
        return;
      }
      const reachable = await probeApi();
      if (!cancelled) setIsOnline(reachable);
    }

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    document.addEventListener('visibilitychange', runProbe);

    void runProbe();
    const interval = window.setInterval(runProbe, PROBE_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', runProbe);
      window.clearInterval(interval);
    };
  }, []);

  return isOnline;
}
